import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
	AbstractControl,
	FormControl,
	FormGroup,
	ReactiveFormsModule,
	Validators,
} from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	AlertComponent,
	DividerComponent,
	FormGroupComponent,
	InputComponent,
	ISelect,
	IStepProgress,
	ItemComponent,
	LinkComponent,
	ListDetailComponent,
	ModalComponent,
	SelectComponent,
	StepperMicroComponent,
	SwitchComponent,
	TileComponent,
} from '@tecalisdev/infinity';
import { RetentionDurationInputComponent } from '../../../../../shared/components/retention-duration-input/retention-duration-input.component';
import { LoadingService } from '../../../../../shared/services/loading.service';
import { MasterService } from '../../../../../shared/services/master.service';
import { ReactiveFormsService } from '../../../../../shared/services/reactive-forms.service';
import { SnackbarService } from '../../../../../shared/services/snackbar.service';
import { RetentionDurationApiValue } from '../../../../../shared/utils/retention-duration.util';
import {
	ICompaniesCreatePayload,
	ICompaniesDetailBody,
	ICompaniesDetailResponse,
	ICompaniesUpdatePayload,
} from '../../models/companies.models';
import { CompaniesService } from '../../services/companies.service';

type CompanyFormStep = 'info' | 'config';

@Component({
	selector: 'app-companies-form',
	imports: [
		ReactiveFormsModule,
		TranslatePipe,
		ModalComponent,
		AlertComponent,
		FormGroupComponent,
		InputComponent,
		SwitchComponent,
		TileComponent,
		DividerComponent,
		SelectComponent,
		StepperMicroComponent,
		ItemComponent,
		ListDetailComponent,
		LinkComponent,
		RetentionDurationInputComponent,
	],
	templateUrl: './companies-form.component.html',
	styleUrl: './companies-form.component.scss',
})
export class CompaniesFormComponent implements OnInit {
	private loading = inject(LoadingService);
	private trans = inject(TranslateService);
	private snackbarService = inject(SnackbarService);
	private companiesService = inject(CompaniesService);
	private readonly destroyRef = inject(DestroyRef);
	public reactiveFormService = inject(ReactiveFormsService);
	public masterService = inject(MasterService);

	@Input() id?: string;
	@Output() closeEvent = new EventEmitter<boolean>();

	public detail?: ICompaniesDetailBody;
	public errorMsg: string = '';
	/** Referencia estable de países (mismo patrón que workflows-form) para que el buscador de tec-select filtre bien. */
	public allCountries: ISelect[] = this.masterService.countries ?? [];
	private previousApikeyValue: boolean = false;
	private previousDesignValue: boolean = false;
	public isSaving = signal(false);
	public currentStep = signal<CompanyFormStep>('info');
	public steps: IStepProgress[] = [];
	public form: FormGroup = new FormGroup({
		name: new FormControl('', Validators.required),
		code: new FormControl('', Validators.required),
		manual_validation: new FormControl(false),
		document_retention_days: new FormControl<RetentionDurationApiValue>(null),
		media_retention_days: new FormControl<RetentionDurationApiValue>(null),
		info_retention_days: new FormControl<RetentionDurationApiValue>(null),
		profile_uuids: new FormControl([]),
		default_api_key: new FormControl(true),
		default_design_config: new FormControl(true),
		country: new FormControl<ISelect | string | null>(null),
	});

	ngOnInit(): void {
		this.refreshCountryOptions();
		this.initSteps();
		this.setupCodeUppercase();
		this.trans.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.refreshCountryOptions();
			this.syncCountryFormControlFromDetail();
			this.initSteps();
		});
		if (this.id) this.getDetail();
		this.setupProfilesUuidListener();
	}

	get isCreateMode(): boolean {
		return !this.id;
	}

	get buttonPrimaryLabel(): string {
		if (this.isSaving()) {
			return this.trans.instant('COMPANIES.FORM.SAVING');
		}
		if (this.isCreateMode && this.currentStep() === 'info') {
			return this.trans.instant('BUTTON.CONTINUE');
		}
		return this.trans.instant('BUTTON.SAVE');
	}

	get buttonSecondaryLabel(): string {
		if (this.isCreateMode && this.currentStep() === 'config') {
			return this.trans.instant('COMPANIES.FORM.BACK');
		}
		return this.trans.instant('BUTTON.CANCEL');
	}

	private initSteps(): void {
		this.steps = [
			{
				id: 'info',
				label: this.trans.instant('COMPANIES.FORM.STEP_INFO'),
				selected: this.currentStep() === 'info',
			},
			{
				id: 'config',
				label: this.trans.instant('COMPANIES.FORM.STEP_CONFIG'),
				selected: this.currentStep() === 'config',
			},
		];
	}

	/**
	 * Fuerza mayúsculas en el código de facturación preservando la posición del caret.
	 * `tec-input` rebindéa `[value]` y, sin restaurar selection, el puntero salta al final.
	 */
	private setupCodeUppercase(): void {
		this.form
			.get('code')
			?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((value: string) => {
				if (value == null || value === '') {
					return;
				}
				const upper = String(value).toUpperCase();
				if (upper === value) {
					return;
				}

				const active = document.activeElement;
				const input = active instanceof HTMLInputElement ? active : null;
				const selectionStart = input?.selectionStart ?? null;
				const selectionEnd = input?.selectionEnd ?? null;

				this.form.get('code')?.setValue(upper, { emitEvent: false });

				if (input && selectionStart != null && selectionEnd != null) {
					setTimeout(() => {
						if (document.activeElement === input) {
							input.setSelectionRange(selectionStart, selectionEnd);
						}
					});
				}
			});
	}

	private refreshCountryOptions(): void {
		this.allCountries = this.masterService.countries ?? [];
	}

	private syncCountryFormControlFromDetail(): void {
		if (!this.detail?.country) {
			return;
		}
		this.form.patchValue({ country: this.resolveCountrySelect(this.detail.country) }, { emitEvent: false });
	}

	private setupProfilesUuidListener(): void {
		this.form.get('profile_uuids')?.valueChanges.subscribe((value: ISelect[] | any[]) => {
			const hasProfiles = value && Array.isArray(value) && value.length > 0;
			const apikeyControl = this.form.get('default_api_key');
			const designControl = this.form.get('default_design_config');

			if (hasProfiles) {
				if (!apikeyControl?.disabled) {
					this.previousApikeyValue = apikeyControl?.value ?? false;
				}
				if (!designControl?.disabled) {
					this.previousDesignValue = designControl?.value ?? false;
				}

				apikeyControl?.setValue(true, { emitEvent: false });
				designControl?.setValue(true, { emitEvent: false });
				apikeyControl?.disable({ emitEvent: false });
				designControl?.disable({ emitEvent: false });
			} else {
				apikeyControl?.setValue(this.previousApikeyValue, { emitEvent: false });
				designControl?.setValue(this.previousDesignValue, { emitEvent: false });
				apikeyControl?.enable({ emitEvent: false });
				designControl?.enable({ emitEvent: false });
			}
		});
	}

	private getDetail(): void {
		this.loading.show(this.trans.instant('LOADING.GET_COMPANY'));
		this.companiesService.detail(this.id).subscribe({
			next: (response: ICompaniesDetailResponse) => {
				this.detail = response.data;
				this.form.patchValue({
					...this.detail,
					code: this.detail.code ? String(this.detail.code).toUpperCase() : '',
					country: this.resolveCountrySelect(this.detail.country),
				});
			},
			error: () => {
				this.errorMsg = this.trans.instant(`COMPANIES.LIST.DETAIL_ERROR`);
				this.loading.hide();
			},
			complete: () => {
				this.loading.hide();
			},
		});
	}

	public clickStep(step: IStepProgress): void {
		if (!this.isCreateMode) {
			return;
		}
		if (step.id === 'config' && this.currentStep() === 'info') {
			this.goToConfigStep();
			return;
		}
		this.setStep(step.id as CompanyFormStep);
	}

	private goToConfigStep(): void {
		const infoControls = ['name', 'code', 'default_api_key', 'default_design_config'];
		let invalid = false;
		for (const name of infoControls) {
			const control = this.form.get(name);
			if (control?.invalid) {
				control.markAsTouched();
				invalid = true;
			}
		}
		if (invalid) {
			return;
		}
		this.setStep('config');
	}

	private setStep(step: CompanyFormStep): void {
		this.currentStep.set(step);
		this.steps = this.steps.map((item) => ({
			...item,
			selected: item.id === step,
		}));
	}

	/** Cierra el modal de creación (Figma paso 2: link encima de Atrás/Guardar). */
	public cancelCreate(event?: Event): void {
		event?.preventDefault();
		event?.stopPropagation();
		if (this.isSaving()) {
			return;
		}
		this.closeEvent.emit(false);
	}

	public closeModal(type: string): void {
		if (this.isSaving()) {
			return;
		}

		if (type === 'buttonPrimary') {
			if (this.isCreateMode && this.currentStep() === 'info') {
				this.goToConfigStep();
				return;
			}
			if (this.form.invalid) {
				this.form.markAllAsTouched();
				return;
			}
			this.detail ? this.updateCompany() : this.createCompany();
			return;
		}

		// Paso 2 crear: secundario = Atrás (Figma); Cancelar va por tec-link.
		if (type === 'buttonSecondary' && this.isCreateMode && this.currentStep() === 'config') {
			this.setStep('info');
			return;
		}

		this.closeEvent.emit(false);
	}

	private updateCompany(): void {
		this.submitCompanyRequest('UPDATE');
	}

	private createCompany(): void {
		this.submitCompanyRequest('CREATE');
	}

	private submitCompanyRequest(requestAction: 'CREATE' | 'UPDATE'): void {
		if (this.isSaving()) {
			return;
		}
		this.isSaving.set(true);
		this.errorMsg = '';
		const body = this.stripNullRetentionOnCreate(this.getPayload(), requestAction === 'CREATE');

		const request$ =
			requestAction === 'CREATE'
				? this.companiesService.create(body as ICompaniesCreatePayload)
				: this.companiesService.update(this.detail!.uuid, body as ICompaniesUpdatePayload);

		request$.subscribe({
			next: (_response: unknown) => {
				this.closeEvent.emit(true);
				this.snackbarService.show(this.trans.instant(`COMPANIES.LIST.${requestAction}_CONFIRM`));
			},
			error: (error: HttpErrorResponse) => {
				this.errorMsg = error.error?.message || this.trans.instant(`COMPANIES.LIST.${requestAction}_ERROR`);
				this.isSaving.set(false);
			},
			complete: () => {
				this.isSaving.set(false);
			},
		});
	}

	private getPayload(): ICompaniesCreatePayload | ICompaniesUpdatePayload {
		const country = this.normalizeCountryPayload(this.form.get('country')?.value);
		const raw = this.form.getRawValue();
		const code = raw.code ? String(raw.code).toUpperCase() : raw.code;

		return {
			...raw,
			code,
			country,
			profile_uuids: this.form.get(`profile_uuids`).value?.map((item: ISelect) => (item.id ? item.id : item)),
			enabled: this.id ? this.detail.enabled : true,
			document_retention_days: this.normalizeRetentionPayload(raw.document_retention_days),
			media_retention_days: this.normalizeRetentionPayload(raw.media_retention_days),
			info_retention_days: this.normalizeRetentionPayload(raw.info_retention_days),
		};
	}

	/**
	 * «No borrar» → null (no duración); «Al momento» → 0; timed → `{n}m|h|d|a`.
	 * En create, las claves con null se omiten para no enviar nada.
	 */
	private normalizeRetentionPayload(value: RetentionDurationApiValue | undefined): RetentionDurationApiValue {
		if (value === null || value === undefined || value === '') {
			return null;
		}
		return value;
	}

	private stripNullRetentionOnCreate(
		payload: ICompaniesCreatePayload | ICompaniesUpdatePayload,
		isCreate: boolean,
	): ICompaniesCreatePayload | ICompaniesUpdatePayload {
		if (!isCreate) {
			return payload;
		}
		const next = { ...payload };
		for (const key of ['document_retention_days', 'media_retention_days', 'info_retention_days'] as const) {
			if (next[key] === null) {
				delete next[key];
			}
		}
		return next;
	}

	private resolveCountrySelect(countryCode?: string | null): ISelect | null {
		if (!countryCode) {
			return null;
		}

		return this.allCountries.find((country: ISelect) => country.id === countryCode) ?? null;
	}

	private normalizeCountryPayload(value: ISelect | string | null | undefined): string | null {
		if (value == null || value === '') {
			return null;
		}

		if (typeof value === 'object' && 'id' in value) {
			return value.id ? String(value.id) : null;
		}

		return String(value);
	}

	public retentionHasError(control: AbstractControl | null): boolean {
		if (!control) {
			return false;
		}
		return control.touched && control.invalid;
	}
}
